import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import {
  BookOpen, Search, Compass, ShieldCheck, LogIn, LayoutDashboard, Route as RouteIcon,
  CalendarRange, Users, Truck, Clock, CalendarClock, UserCog, FileText, Lightbulb,
  Copy, Pencil, ChevronRight, Plane, Stethoscope, UserCircle, AlertTriangle,
} from "lucide-react";

/* ---------- Small presentational helpers ---------- */
const Lead = ({ children }) => <p className="text-[15px] leading-relaxed text-muted-foreground">{children}</p>;
const P = ({ children }) => <p className="text-sm leading-relaxed text-foreground/90">{children}</p>;
const H3 = ({ children }) => <h3 className="text-base font-semibold heading mt-5 mb-1.5">{children}</h3>;
const UL = ({ children }) => <ul className="list-disc pl-5 space-y-1 text-sm text-foreground/90 marker:text-blue-500">{children}</ul>;
const Steps = ({ children }) => <ol className="list-decimal pl-5 space-y-1.5 text-sm text-foreground/90 marker:text-blue-500 marker:font-semibold">{children}</ol>;
const Kbd = ({ children }) => <span className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] font-medium tabular-nums">{children}</span>;
const Pill = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-200",
    green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200",
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200",
    violet: "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-200",
    zinc: "bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200",
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>{children}</span>;
};
const Note = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "border-blue-300 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40",
    amber: "border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40",
    green: "border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40",
  };
  return <div className={`rounded-md border-l-4 px-3 py-2 text-sm ${tones[tone]}`}>{children}</div>;
};

/* ---------- Role groups (developer always sees everything) ---------- */
const ALL = ["admin", "disponent", "fahrer"];
const STAFF = ["admin", "disponent"];
const ADMIN = ["admin"];

/* ---------- Documentation content ---------- */
const SECTIONS = [
  /* ===== Sections for all roles ===== */
  {
    id: "ueberblick", title: "Überblick", icon: Compass, roles: ALL, keywords: "einführung start willkommen system überblick",
    body: (
      <>
        <Lead>Willkommen im Handbuch des Personenbeförderung-Verwaltungssystems. Hier findest du alles, um Fahrten zu planen oder einzusehen, Stammdaten zu pflegen und Arbeitszeiten zu erfassen bzw. nachzuvollziehen.</Lead>
        <H3>Was kann das System?</H3>
        <UL>
          <li><b>Wochenplanung</b> – Fahrten anlegen, bearbeiten, per Drag &amp; Drop in der Timeline verschieben (auch <i>tagesübergreifend</i> und <i>zwischen Fahrer-Spuren</i>) und auf andere Tage kopieren. Inklusive einer Fahrer-Wochenbilanz (Touren + geplante Stunden).</li>
          <li><b>Fahrtenübersicht</b> – alle Fahrten suchen, filtern, exportieren (CSV/PDF), drucken und Status setzen.</li>
          <li><b>Stammdaten</b> – Fahrer (mit Zeitkonto-Kategorie) und Fahrzeuge pflegen.</li>
          <li><b>Arbeitszeit</b> – Schichten manuell oder per Start/Stopp-Uhr erfassen (mit Vorbefüllung aus dem Arbeitszeitmodell), Pausen, Urlaube und Krankheitstage protokollieren. Abgeschlossene Fahrten fließen automatisch ein.</li>
          <li><b>Arbeitszeitübersicht</b> – grafische Wochen- oder Monatsübersicht je Fahrer (Arbeit/Urlaub/Krank) mit Export je Zeitraum.</li>
          <li><b>Arbeitszeitmodell &amp; Zeitkonto-Kategorien</b> – Vorlagen je Wochentag pro Kategorie (Vollzeit/Teilzeit/…).</li>
          <li><b>Verwaltung</b> – Benutzer anlegen, Rollen vergeben, alle Änderungen im Audit-Log nachvollziehen.</li>
          <li><b>Helle/dunkle Oberfläche</b> – per Klick umschaltbar, pro Gerät gespeichert.</li>
        </UL>
        <Note tone="blue">Tipp: Nutze die Suche oben links in den Docs, um schnell ein Thema zu finden – oder die globale Suche in der Topbar, um direkt zu einer Fahrt zu springen.</Note>
      </>
    ),
  },
  {
    id: "rollen", title: "Rollen & Rechte", icon: ShieldCheck, roles: ALL, keywords: "rolle rechte admin disponent fahrer berechtigung",
    body: (
      <>
        <Lead>Jeder Benutzer hat eine Rolle. Sie bestimmt, welche Menüpunkte sichtbar sind und was bearbeitet werden darf.</Lead>
        <H3>Die drei Rollen</H3>
        <UL>
          <li><Pill>Admin</Pill> – Vollzugriff inkl. Benutzerverwaltung und Audit-Log.</li>
          <li><Pill tone="green">Disponent</Pill> – plant Fahrten, verwaltet Fahrer/Fahrzeuge, Arbeitszeitmodell, Urlaube und Krankheitstage.</li>
          <li><Pill tone="zinc">Fahrer</Pill> – sieht ausschließlich die eigenen Fahrten sowie die eigene Arbeitszeit und kann den Status der eigenen Fahrten ändern.</li>
        </UL>
        <Note tone="amber">Fahrer dürfen Fahrten nicht anlegen oder löschen – nur den Status (z.&nbsp;B. „Unterwegs", „Abgeschlossen") setzen. Urlaubs- und Krankheitstage werden vom Disponenten/Admin eingetragen.</Note>
      </>
    ),
  },
  {
    id: "anmeldung", title: "Anmeldung", icon: LogIn, roles: ALL, keywords: "login anmelden passwort abmelden",
    body: (
      <>
        <Lead>Der Zugang erfolgt über Benutzername und Passwort.</Lead>
        <Steps>
          <li>Öffne die Startseite – du landest automatisch auf der Anmeldung.</li>
          <li>Gib <b>Benutzername</b> und <b>Passwort</b> ein und klicke auf <b>Anmelden</b>.</li>
          <li>Nach erfolgreicher Anmeldung erscheint dein Dashboard.</li>
        </Steps>
        <H3>Abmelden & Theme</H3>
        <P>Unten links im Menü findest du <b>Abmelden</b>. Über das Sonnen-/Mond-Symbol oben rechts wechselst du zwischen hellem und dunklem Design.</P>
        <Note tone="amber">Passwort vergessen? Bitte deinen Administrator, dir ein neues Passwort zu vergeben.</Note>
      </>
    ),
  },
  {
    id: "dashboard", title: "Dashboard", icon: LayoutDashboard, roles: ALL, keywords: "dashboard übersicht kennzahlen statistik startseite kpi widgets",
    body: (
      <>
        <Lead>Das Dashboard ist deine Startseite mit den wichtigsten Kennzahlen auf einen Blick. Die Inhalte richten sich nach deiner Rolle.</Lead>

        <H3>Admin &amp; Disponent</H3>
        <UL>
          <li><b>Kennzahl-Kacheln:</b> Fahrten heute, geplante Fahrten, aktive Fahrer, aktive Fahrzeuge.</li>
          <li><b>Heutige Fahrten</b> als Tabelle – Klick führt direkt in die Fahrtenliste mit der entsprechenden Fahrt im Fokus.</li>
          <li>Schnellzugriff <b>Wochenplanung öffnen</b>.</li>
        </UL>

        <H3>Fahrer</H3>
        <UL>
          <li><b>„Meine Tour heute":</b> alle deine Fahrten für den aktuellen Tag als Karten – inklusive aller Abhol-/Zieladressen, Bemerkungen und Zeiten.</li>
          <li>Zu jeder Karte gibt es <b>Status-Buttons</b>: „Tour starten" setzt den Status auf <Pill tone="amber">Unterwegs</Pill>, „Fertig" auf <Pill tone="green">Abgeschlossen</Pill>.</li>
          <li>Keine Touren heute? Das Dashboard zeigt einen freundlichen „Genieß den Tag"-Hinweis.</li>
        </UL>
      </>
    ),
  },
  {
    id: "topbar", title: "Topbar & Navigation", icon: Search, roles: ALL, keywords: "topbar kopfzeile suche global navigation menü sidebar dunkel hell theme abmelden",
    body: (
      <>
        <Lead>Die obere Kopfzeile bündelt häufige Aktionen, die dir überall im System zur Verfügung stehen.</Lead>
        <H3>Elemente</H3>
        <UL>
          <li><b>Menü-Knopf (links):</b> blendet die Seitenleiste auf kleinen Bildschirmen ein/aus.</li>
          <li><b>Globale Suche (Admin/Disponent):</b> Tippe einen Namen, eine Personalnummer, Telefonnummer oder ein Kennzeichen ein – in einem <b>Live-Dropdown</b> erscheinen passende <b>Fahrer</b> und <b>Fahrzeuge</b>. Ein Klick öffnet sofort die jeweilige <b>Detailkarte</b> – ohne Umweg über eine Listenseite. Über „Fahrten durchsuchen nach …" (oder Enter) springst du zusätzlich in die gefilterte Fahrten-Liste.</li>
          <li><b>Globale Suche (Fahrer):</b> Fahrer nutzen eine einfache Fahrten-Suche – Begriff eingeben und Enter führt in die eigene Fahrten-Liste.</li>
          <li><b>Theme-Schalter (Sonne/Mond):</b> wechselt zwischen hellem und dunklem Design; die Auswahl bleibt pro Gerät gespeichert.</li>
        </UL>
        <H3>Seitenleiste</H3>
        <UL>
          <li>Listet alle für deine Rolle verfügbaren Bereiche – in dieser Reihenfolge: Dashboard, Fahrten, Fahrer, Fahrzeuge, Wochenplanung, <b>Arbeitszeit</b> (ausklappbar), Benutzerverwaltung, Audit-Log und Docs.</li>
          <li>Der Punkt <b>Arbeitszeit</b> ist <b>ausklappbar</b> und enthält <b>Arbeitszeiterfassung</b>, <b>Arbeitszeitübersicht</b> und <b>Arbeitszeitmodell</b>.</li>
          <li>Unten: dein Name, deine Rolle und der <b>Abmelden</b>-Knopf.</li>
          <li>Auf Tablets/Smartphones lässt sich die Seitenleiste über das Menü-Symbol ein- und ausklappen.</li>
        </UL>
      </>
    ),
  },

  /* ===== Driver-focused sections ===== */
  {
    id: "meine-fahrten", title: "Meine Fahrten", icon: RouteIcon, roles: ["fahrer"], keywords: "fahrten meine touren status abgeschlossen unterwegs starten fertig",
    body: (
      <>
        <Lead>Im Bereich <b>Fahrten</b> siehst du ausschließlich die dir zugewiesenen Touren – sortiert nach Datum und Startzeit.</Lead>

        <H3>Was du tun kannst</H3>
        <UL>
          <li><b>Details ansehen:</b> Abhol- und Zieladressen, Bemerkungen und Besonderheiten sind direkt sichtbar – auch wenn eine Tour mehrere Stopps hat.</li>
          <li><b>Status ändern:</b> Setze deine Fahrten auf <Pill tone="amber">Unterwegs</Pill> bei Antritt und auf <Pill tone="green">Abgeschlossen</Pill> nach der Rückkehr. Das geht über das Aktions-Menü (⋮) am Zeilenende.</li>
          <li><b>Schnell-Buttons auf dem Dashboard:</b> Für die heutigen Touren genügt ein Klick auf „Tour starten" bzw. „Fertig" – kein Umweg über die Liste nötig.</li>
          <li><b>Suchen &amp; filtern:</b> Über das Suchfeld nach Adresse oder Bemerkung filtern; Spalten per Klick auf den Kopf sortieren.</li>
        </UL>

        <H3>Statusbedeutung</H3>
        <UL>
          <li><Pill tone="blue">Geplant</Pill> – Fahrt ist eingeteilt, aber noch nicht gestartet.</li>
          <li><Pill tone="amber">Unterwegs</Pill> – du bist auf der Tour.</li>
          <li><Pill tone="green">Abgeschlossen</Pill> – die Tour ist beendet.</li>
          <li><Pill tone="zinc">Storniert</Pill> – die Fahrt findet nicht statt.</li>
        </UL>

        <Note tone="amber">Du kannst <b>keine</b> neuen Fahrten anlegen, Fahrer/Fahrzeug ändern oder Fahrten löschen. Wende dich dafür an deinen Disponenten.</Note>
      </>
    ),
  },
  {
    id: "meine-arbeitszeit", title: "Meine Arbeitszeit", icon: Clock, roles: ["fahrer"], keywords: "arbeitszeit stunden pause schicht ansicht",
    body: (
      <>
        <Lead>Unter <b>Arbeitszeiterfassung</b> findest du deine eigenen Arbeitszeiten als reine Übersicht.</Lead>
        <H3>Was du siehst</H3>
        <UL>
          <li><b>Summen oben:</b> heutige, wöchentliche und monatliche Arbeitszeit.</li>
          <li><b>Verlauf:</b> alle deine erfassten Schichten mit Start, Ende, Dauer, Pause und Anzahl Fahrten – wählbar nach <i>Tag</i>, <i>Woche</i>, <i>Monat</i> oder <i>Alle</i>.</li>
          <li><b>Urlaubs- und Krankheitstage</b> sind im Verlauf farbig hervorgehoben.</li>
          <li>Offene Schichten (ohne Endzeit) sind mit „offen" markiert.</li>
        </UL>
        <Note tone="amber">Die Erfassung deiner Arbeitszeit erfolgt nicht mehr per <b>Start/Stop</b>-Button. Schichten werden vom Disponenten/Admin eingetragen bzw. ergänzt. Solltest du eine Korrektur benötigen, melde dich beim Disponenten.</Note>
      </>
    ),
  },
  {
    id: "urlaub-fahrer", title: "Urlaub & Krankheit beantragen", icon: Plane, roles: ["fahrer"], keywords: "urlaub krank krankheit beantragen abwesenheit fehltag",
    body: (
      <>
        <Lead>Urlaube und Krankheitstage werden zentral vom Disponenten/Admin gepflegt – so bleibt die Planung konsistent.</Lead>
        <H3>So beantragst du Urlaub</H3>
        <Steps>
          <li>Wende dich (mündlich, telefonisch oder per Nachricht) an deinen <b>Disponenten</b> bzw. <b>Administrator</b>.</li>
          <li>Nenne den gewünschten <b>Zeitraum</b> (von – bis) und eine kurze Begründung, falls erforderlich.</li>
          <li>Nach Eintragung erscheint der Zeitraum in deinem Verlauf unter <b>Arbeitszeiterfassung</b> als <Pill tone="blue">Urlaub</Pill> oder <Pill tone="amber">Krank</Pill>.</li>
        </Steps>
        <H3>Im Krankheitsfall</H3>
        <UL>
          <li>Melde dich umgehend telefonisch beim Disponenten.</li>
          <li>Sobald die Krankschreibung eingetragen ist, werden für diesen Zeitraum keine neuen Fahrten geplant und du erscheinst in der Wochenplanung als „abwesend".</li>
        </UL>
        <Note tone="green">Im Verlauf der Arbeitszeit kannst du jederzeit prüfen, welche Urlaubs- und Krankheitstage für dich erfasst wurden.</Note>
      </>
    ),
  },
  {
    id: "eigene-daten", title: "Eigene Daten & Konto", icon: UserCircle, roles: ["fahrer"], keywords: "konto passwort eigene daten profil theme dunkel hell logout abmelden",
    body: (
      <>
        <Lead>Dein Konto verknüpft dich mit deinem Fahrer-Datensatz – nur so siehst du genau deine Fahrten und Arbeitszeiten.</Lead>
        <H3>Wo finde ich meine Daten?</H3>
        <UL>
          <li>Unten links im Menü stehen dein <b>Name</b>, deine <b>Rolle</b> und der <b>Abmelden</b>-Knopf.</li>
          <li>In den Docs (diese Seite) wird unter der Navigation angezeigt, als wer du angemeldet bist.</li>
          <li>Stammdaten (Name, Personalnummer, Telefonnummer, Adresse, Notfallkontakt) werden vom Disponenten/Admin im Bereich <b>Fahrer</b> gepflegt.</li>
        </UL>
        <H3>Was kann ich selbst anpassen?</H3>
        <UL>
          <li><b>Design wechseln:</b> Über das Sonnen-/Mond-Symbol oben rechts schaltest du zwischen hellem und dunklem Theme um – deine Wahl bleibt auf dem Gerät gespeichert.</li>
          <li><b>Status der eigenen Fahrten:</b> direkt in der Fahrtenliste setzen.</li>
        </UL>
        <Note tone="amber">Stimmen deine Stammdaten nicht? Bitte deinen Disponenten oder Administrator um Korrektur. Möchtest du dein Passwort ändern, wende dich ebenfalls an den Administrator – er kann ein neues vergeben.</Note>
      </>
    ),
  },

  /* ===== Staff sections ===== */
  {
    id: "fahrten", title: "Fahrten", icon: RouteIcon, roles: STAFF, keywords: "fahrten liste suche filter export pdf csv drucken status mehrere adressen abhol ziel stundenzusammenfassung",
    body: (
      <>
        <Lead>Die Fahrtenübersicht listet alle Fahrten – ideal zum Suchen, Filtern, Bearbeiten und Exportieren.</Lead>

        <H3>Suchen &amp; filtern</H3>
        <UL>
          <li><b>Suchfeld:</b> findet Treffer in Fahrgast, Fahrer, Linie und Ortsangabe.</li>
          <li><b>Status-Filter</b> (Dropdown rechts neben Suche): nur Geplant, Unterwegs, Abgeschlossen oder Storniert anzeigen.</li>
          <li>Spalten per Klick auf die Überschrift sortieren (Datum, Start, Fahrer …).</li>
          <li>Die globale Suche in der Topbar springt mit einem Treffer direkt in die passende Fahrt.</li>
        </UL>

        <H3>Mehrere Adressen je Fahrt</H3>
        <P>Eine Fahrt kann beliebig viele <b>Abhol-</b> und <b>Zieladressen</b> haben (Sammelfahrten, Zwischenstopps). Pro Adresse lassen sich Bemerkungen erfassen. Die Reihenfolge wird in der Detailansicht so dargestellt, wie sie eingegeben wurde.</P>

        <H3>Bearbeiten, Status &amp; Löschen</H3>
        <UL>
          <li>Über das <b>Aktions-Menü</b> (⋮ am Zeilenende) → <b>Bearbeiten</b>, <b>Status setzen</b> (<Pill tone="blue">Geplant</Pill> · <Pill tone="amber">Unterwegs</Pill> · <Pill tone="green">Abgeschlossen</Pill> · <Pill tone="zinc">Storniert</Pill>) oder <b>Löschen</b>.</li>
          <li>Klick auf den Fahrernamen einer Zeile öffnet das <b>Fahrer-Detail</b> – mit dem Tag der Fahrt voreingestellt.</li>
        </UL>

        <H3>Stundenzusammenfassung</H3>
        <P>Unten in der Übersicht (sofern Filter „Abgeschlossen" oder Status-übergreifend) zeigt der Bereich „Arbeitszeit-Summe" geplante / abgeschlossene Stunden je Fahrer – nützlich für die Tages- oder Wochenauswertung.</P>

        <H3>Exportieren &amp; drucken</H3>
        <P>Beim Klick auf <b>Export → CSV</b> oder <b>PDF</b> öffnet sich zuerst ein <b>Filter-Dialog</b>. Dort wählst du entweder <b>„Alles exportieren"</b> oder kombinierst gezielt Kategorien:</P>
        <UL>
          <li><b>Fahrer</b>, <b>Linie</b>, <b>Kennzeichen</b> und <b>Status</b> als Mehrfachauswahl.</li>
          <li><b>Zeitraum</b> von–bis sowie <b>Adressen</b> (Orte/Straßen, mit Komma getrennt).</li>
          <li>Innerhalb einer Kategorie gilt <b>ODER</b>, zwischen den Kategorien <b>UND</b> – z.&nbsp;B. „(Fahrer A oder B) und Status Abgeschlossen".</li>
          <li>Mit <b>Exportieren</b> startet der Download im zuvor gewählten Format (CSV oder PDF).</li>
        </UL>
        <P>Hinweis: Die Spalte „Fahrzeug" heißt jetzt <b>Linie</b> (entspricht der Linienbezeichnung des Fahrzeugs).</P>

        <Note tone="amber">
          <span className="inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Neue Fahrten werden nicht hier angelegt.</span>{" "}
          Das Anlegen läuft ausschließlich über die <b>Wochenplanung</b> – dort hast du den Wochenkontext und das Drag-&amp;-Drop zur Verfügung.
        </Note>
      </>
    ),
  },
  {
    id: "wochenplanung", title: "Wochenplanung", icon: CalendarRange, roles: STAFF, keywords: "wochenplanung planen fahrt anlegen bearbeiten kopieren timeline liste drag drop verschieben",
    body: (
      <>
        <Lead>Das Herzstück der Planung: alle Fahrten einer Woche als <b>Liste</b> oder als <b>Stunden-Timeline</b>. Hier werden Fahrten angelegt, bearbeitet und auf andere Tage kopiert.</Lead>

        <H3>Woche wählen & Ansicht</H3>
        <UL>
          <li>Mit <ChevronRight className="inline h-3.5 w-3.5" /> / Pfeilen oder dem Kalender zwischen Wochen wechseln, <b>Aktuelle Woche</b> springt zurück zu heute.</li>
          <li>Oben rechts zwischen <b>Liste</b> und <b>Timeline</b> umschalten.</li>
          <li>In der Timeline stellst du oben rechts den <b>Zoom</b> ein (30&nbsp;Min · 1&nbsp;Std · 6&nbsp;Std · 12&nbsp;Std · 24&nbsp;Std). Er skaliert die Zeitleiste horizontal – bei feinem Zoom kannst du seitlich scrollen. Die Auswahl bleibt pro Gerät gespeichert. Das Andock-Raster beim Ziehen liegt fest bei 15&nbsp;Min.</li>
        </UL>

        <H3>Fahrt anlegen</H3>
        <Steps>
          <li>Auf <Kbd>+</Kbd> im Kopf eines Wochentags (bzw. <b>Neu</b> in der Timeline) klicken.</li>
          <li>Beliebig viele <b>Abhol-</b> und <b>Zieladressen</b> mit Bemerkung/Besonderheiten erfassen.</li>
          <li>Datum, Start-/Endzeit, Fahrer, Fahrzeug und Status wählen.</li>
          <li>Mit <b>Planen</b> speichern.</li>
        </Steps>

        <H3>Fahrt bearbeiten</H3>
        <UL>
          <li>Eine Fahrtenkarte (oder einen Timeline-Balken) anklicken öffnet den Bearbeiten-Dialog.</li>
          <li>Fahrer, Fahrzeug und Status lassen sich direkt auf der Karte über die Auswahlfelder ändern.</li>
          <li>Konflikte (gleicher Fahrer/Fahrzeug zur gleichen Zeit) werden in der Timeline farbig hervorgehoben (Fahrer = rot, Fahrzeug = gelb).</li>
        </UL>

        <H3>Fahrer-Übersicht der Woche</H3>
        <P>Direkt unter der Wochen-Navigation siehst du in beiden Ansichten (Liste &amp; Timeline) eine kompakte Übersicht <b>aller aktiven Fahrer</b> mit ihrer Wochenbilanz:</P>
        <UL>
          <li><b>Touren-Anzahl</b> in der ausgewählten Woche.</li>
          <li><b>Geplante Stunden</b> (Summe aus Start–Ende aller Fahrten dieser Woche).</li>
          <li>Fahrer ohne Touren in der Woche werden dezent dargestellt (Trips = 0, 0 h).</li>
          <li>Gibt es Fahrten <b>ohne Fahrer</b>, erscheint zusätzlich ein Eintrag „ohne Fahrer".</li>
          <li>Oben rechts erscheint die <b>Gesamt-Summe</b> (Touren · geplante Stunden) für die Woche.</li>
        </UL>

        <H3>Timeline – Aufbau</H3>
        <P>In der Timeline bekommt <b>jeder Fahrer pro Tag eine eigene Spur</b>. So bleiben auch zwei, vier oder mehr Fahrten am selben Tag übersichtlich. Fahrten ohne Fahrer landen in einer eigenen Spur „ohne Fahrer" am unteren Rand des Tages.</P>

        <H3>Timeline – Drag &amp; Drop</H3>
        <UL>
          <li><b>Balken ziehen:</b> verschiebt eine Fahrt entlang der Zeitachse (im gewählten Raster).</li>
          <li><b>Auf einen anderen Tag ziehen:</b> Balken in eine andere Tageszeile bewegen – beim Loslassen wird das Datum der Fahrt automatisch angepasst. Die Zielzeile wird beim Ziehen blau hervorgehoben.</li>
          <li><b>Auf eine andere Fahrer-Spur ziehen:</b> Balken in die Spur eines anderen Fahrers ziehen – Fahrt wird beim Loslassen <i>diesem Fahrer</i> zugewiesen. Auch quer über mehrere Tage möglich (z.&nbsp;B. Anna übernimmt Maxs Tour am Freitag). Die Ziel-Spur wird beim Ziehen blau hervorgehoben.</li>
          <li><b>Auf die „ohne Fahrer"-Spur ziehen:</b> entfernt die Fahrerzuweisung.</li>
          <li><b>Kanten ziehen:</b> Links/rechts an den Bar-Kanten ziehen passt Start- bzw. Endzeit an (mindestens ein Raster-Schritt).</li>
          <li>Bei einer Verschiebung mit Zeitkonflikt erscheint eine Sicherheitsabfrage. Beim Fahrer-Wechsel wird die Frage übersprungen, da dies eine bewusste Umbuchung ist.</li>
        </UL>

        <H3>Liste – Drag &amp; Drop</H3>
        <P>Auch in der Listenansicht kannst du eine Fahrtenkarte per Drag &amp; Drop in eine andere Tagesspalte ziehen – das Datum wird automatisch übernommen.</P>

        <H3><span className="inline-flex items-center gap-1.5"><Copy className="h-4 w-4 text-blue-600" /> Auf andere Tage kopieren</span></H3>
        <P>So überträgst du eine Fahrt schnell auf mehrere Tage – z.&nbsp;B. eine täglich wiederkehrende Tour.</P>
        <Steps>
          <li>Auf das <Copy className="inline h-3.5 w-3.5" />-Symbol einer Fahrtenkarte klicken – oder im Bearbeiten-Dialog auf <b>Kopieren</b>.</li>
          <li><b>Zieltage wählen:</b> Wochentage der aktuellen Woche per Checkbox <i>oder</i> beliebige Tage im Kalender. Beides ist kombinierbar.</li>
          <li>Schalter <b>„Status auf Geplant zurücksetzen"</b>: aktiv = die Kopien starten als „Geplant"; aus = der Status der Quelle wird übernommen.</li>
          <li>Auf <b>Kopieren</b> klicken. Adressen, Zeiten, Fahrer und Fahrzeug werden mitkopiert.</li>
        </Steps>

        <H3>Abwesenheits-Warnung</H3>
        <P>Ist ein Fahrer am gewählten Tag im Urlaub oder krank, erscheint in der Fahrtenkarte ein gelber Hinweis (<AlertTriangle className="inline h-3 w-3" /> „Fahrer im Urlaub/Krank"). Im Fahrer-Dropdown sind betroffene Personen farbig markiert.</P>

        <Note tone="green">Der Quelltag ist beim Kopieren in der Auswahl deaktiviert, damit keine Dublette am selben Tag entsteht. Ein Zähler zeigt, wie viele Zieltage ausgewählt sind.</Note>
      </>
    ),
  },
  {
    id: "fahrer", title: "Fahrer", icon: Users, roles: STAFF, keywords: "fahrer mitarbeiter stammdaten personalnummer anlegen aktiv kontakt detail tagesübersicht kategorie zeitkonto",
    body: (
      <>
        <Lead>Verwalte hier alle Fahrer als Stammdaten – inklusive Zuordnung zu einer Zeitkonto-Kategorie und Detailansicht je Tag.</Lead>

        <H3>Anlegen &amp; pflegen</H3>
        <UL>
          <li><b>Pflichtangaben:</b> Vorname, Nachname, <b>Telefonnummer</b> und <b>Personalnummer</b> (mit rotem „*" gekennzeichnet und validiert).</li>
          <li><b>Optional:</b> E-Mail sowie die Zuordnung zu einem <b>Arbeitszeitmodell</b> (Zeitkonto-Kategorie, z.&nbsp;B. Vollzeit/Teilzeit – siehe „Arbeitszeitmodell &amp; Zeitkonto-Kategorien").</li>
          <li><b>Aktiv/Inaktiv:</b> Inaktive Fahrer erscheinen nicht mehr in den Auswahllisten der Planung – ihre Historie bleibt erhalten.</li>
          <li><b>Klickbarer Name:</b> Ein Klick auf den Fahrernamen öffnet die Detailkarte (siehe unten).</li>
          <li>Suche per Namens-, E-Mail- oder Personalnummer-Eingabe oben in der Liste.</li>
        </UL>

        <H3>Fahrer-Detail (Klick auf den Namen)</H3>
        <P>Ein Klick auf den Namen eines Fahrers öffnet eine Detailansicht mit:</P>
        <UL>
          <li><b>Übersichts-Diagramm (Kreis):</b> oben links zeigt ein Ring-Diagramm das Verhältnis von <Pill tone="blue">Arbeitstagen</Pill>, <Pill tone="blue">Urlaub</Pill> und <Pill tone="amber">Krank</Pill>. In der Mitte stehen die Arbeitstage, in der Legende zusätzlich die <b>Anzahl der Fahrten</b>. Über den Umschalter <b>Monat / Jahr / Gesamt</b> wählst du den Auswertungszeitraum. Rechts daneben sind die Stammdaten (Personalnr., Status, Telefon, E-Mail) angeordnet.</li>
          <li><b>Tages-Übersicht</b> mit Datums-Navigator: berechnete Schicht (Start, Ende, Dauer), zugewiesene Fahrten, Urlaubs-/Krankheitstage.</li>
          <li><b>Abgeleitete Schichten</b> der letzten Tage – sofort sichtbar, wie viele Stunden tatsächlich anfielen.</li>
          <li>Schneller Sprung zu seiner Arbeitszeit-Historie.</li>
        </UL>

        <H3>Mit Benutzerkonto verknüpfen</H3>
        <P>Damit ein Fahrer sich anmelden und seine Fahrten/Arbeitszeit sehen kann, muss in der <b>Benutzerverwaltung</b> sein Konto mit diesem Fahrer-Datensatz verknüpft werden (Feld „Fahrer verknüpfen").</P>

        <Note tone="amber">Fahrer löschen wird empfohlen, nur wenn es nie Fahrten oder Arbeitszeiten zu diesem Fahrer gab. Andernfalls lieber <b>inaktiv</b> setzen.</Note>
      </>
    ),
  },
  {
    id: "fahrzeuge", title: "Fahrzeuge", icon: Truck, roles: STAFF, keywords: "fahrzeuge auto kennzeichen flotte anlegen aktiv kfz typ modell",
    body: (
      <>
        <Lead>Pflege hier deine Fahrzeugflotte.</Lead>
        <UL>
          <li>Fahrzeuge mit <b>Linien Bezeichnung</b>, <b>Kennzeichen</b> und <b>Typ</b> anlegen. Der <b>Fahrzeugtyp</b> ist ein <b>freies Textfeld</b> – du kannst einen beliebigen Text eintragen (z.&nbsp;B. „Mercedes Sprinter", „Spezialbus 3000"). Das Kennzeichen ist eindeutig.</li>
          <li>Über das Suchfeld nach Linien Bezeichnung, Kennzeichen oder Typ filtern.</li>
          <li><b>Aktiv-Schalter</b> direkt in der Tabelle: inaktive Fahrzeuge sind in der Planung nicht mehr auswählbar, ihre Historie bleibt sichtbar.</li>
          <li><b>Klickbare Bezeichnung/Kennzeichen:</b> öffnen die Fahrzeug-Detailkarte – konsistent mit den übrigen Ansichten.</li>
        </UL>
        <Note tone="blue">Ändert sich ein Kennzeichen, kannst du es direkt im Datensatz aktualisieren – bestehende Fahrten behalten den Bezug zum Fahrzeug.</Note>
      </>
    ),
  },
  {
    id: "arbeitszeit", title: "Arbeitszeiterfassung", icon: Clock, roles: STAFF, keywords: "arbeitszeit schicht erfassung pause stunden manuell anlegen bearbeiten offen abschließen auto kategorie",
    body: (
      <>
        <Lead>Zentrale Übersicht und Auswertung der Arbeitszeiten. Arbeitszeiten werden <b>automatisch</b> aus den Fahrten der Wochenplanung erzeugt – hier pflegst du nur noch <b>Urlaub</b> und <b>Krank</b>. Fahrer haben Lesezugriff.</Lead>

        <H3>Woher kommen die Arbeitszeiten?</H3>
        <P>Eine <b>manuelle Neuanlage gibt es nicht mehr</b> – der Button „Neue Arbeitszeit" wurde entfernt. Stattdessen entstehen die Schichten <b>automatisch</b> aus den Fahrten: pro Fahrer und Tag wird aus den Fahrten eine Schicht abgeleitet (Start = früheste Fahrt, Ende = späteste Fahrt), erkennbar am grünen Badge <Pill tone="green">Fahrten</Pill>.</P>
        <UL>
          <li>Berücksichtigt werden Fahrten mit Status <Pill tone="green">Abgeschlossen</Pill> sowie alle Fahrten, deren <b>Datum in der Vergangenheit</b> liegt – auch <b>rückwirkend</b> in der Wochenplanung angelegte Fahrten zählen also automatisch mit.</li>
          <li>Stornierte Fahrten werden nicht gezählt.</li>
          <li>Diese Auto-Einträge sind schreibgeschützt (Anzeige „automatisch") und zählen in die Summen sowie in die Arbeitszeitübersicht.</li>
        </UL>

        <H3>Offene Schichten abschließen</H3>
        <P>In der Verlaufstabelle erscheinen Schichten ohne Ende als <Pill tone="green">offen</Pill>. Mit einem Klick auf „offen" lässt sich die Endzeit direkt nachtragen.</P>

        <H3>Bearbeiten &amp; Löschen</H3>
        <UL>
          <li>Über das Stift-Symbol öffnet sich der Bearbeiten-Dialog (Zeiten, Pausen, Notiz, Fahrzeug).</li>
          <li>Über das Mülltonnen-Symbol kann ein Eintrag nach Bestätigung gelöscht werden.</li>
          <li>Alle Änderungen werden im <b>Audit-Log</b> protokolliert.</li>
        </UL>

        <H3>Automatisch aus abgeschlossenen Fahrten</H3>
        <P>Fahrten mit dem Status <Pill tone="green">Abgeschlossen</Pill> fließen <b>automatisch</b> in die Arbeitszeiterfassung ein: pro Fahrer und Tag entsteht eine abgeleitete Schicht (Start = früheste Fahrt, Ende = späteste Fahrt), erkennbar am grünen Badge <Pill tone="green">Fahrten</Pill>. Diese Auto-Einträge sind schreibgeschützt (Anzeige „automatisch" statt Bearbeiten/Löschen) und zählen in die Summen. Existiert für denselben Tag bereits ein manueller Eintrag, hat dieser Vorrang – es wird <b>nicht doppelt</b> gezählt.</P>

        <H3>Filter &amp; Sicht</H3>
        <UL>
          <li><b>Fahrer-Filter:</b> alle oder einen bestimmten Mitarbeiter anzeigen.</li>
          <li><b>Zeitraum-Tabs:</b> <i>Tag / Woche / Monat / Alle</i>. Im Tages-Tab kann mit der Datums-Navigation auf einen beliebigen Tag gesprungen werden.</li>
          <li><b>Summen oben:</b> Heute / Woche / Monat (immer gefiltert nach Fahrer-Auswahl).</li>
          <li><b>Farbcodes:</b> Urlaub (<Pill tone="blue">Urlaub</Pill>) und Krankheit (<Pill tone="amber">Krank</Pill>) werden farbig hervorgehoben, ohne Stundenanrechnung.</li>
        </UL>
      </>
    ),
  },
  {
    id: "arbeitszeit-uebersicht", title: "Arbeitszeitübersicht", icon: CalendarRange, roles: STAFF, keywords: "arbeitszeitübersicht woche monat kästchen raster fahrer arbeit urlaub krank export csv pdf navigation live",
    body: (
      <>
        <Lead>Grafische Wochen- oder Monatsübersicht je Fahrer: auf einen Blick, wer wann <b>arbeitet</b>, im <b>Urlaub</b> oder <b>krank</b> ist. Die Ansicht aktualisiert sich automatisch (live).</Lead>

        <H3>Aufbau des Rasters</H3>
        <UL>
          <li><b>Zeilen = Fahrer</b>, <b>Spalten = Tage</b>. Jede Zelle ist ein farbiges Kästchen: <Pill tone="blue">Arbeit</Pill> (blau, mit Stunden), <Pill tone="blue">Urlaub</Pill> (hellblau), <Pill tone="amber">Krank</Pill> (orange), leer = frei.</li>
          <li>Die <b>Fahrernamen sind anklickbar</b> und öffnen die Fahrer-Detailkarte.</li>
          <li>Oben zeigen Summenkarten die Arbeits-, Urlaubs- und Kranktage sowie die Gesamt-Arbeitszeit der Periode.</li>
        </UL>

        <H3>Woche &amp; Monat</H3>
        <UL>
          <li>Über die Umschaltung <b>Woche / Monat</b> wechselst du die Ansicht. In der Wochenansicht stehen 7 Tage, in der Monatsansicht alle Tage des Monats.</li>
          <li>Mit den Pfeilen <b>‹ ›</b> blätterst du eine Woche bzw. einen Monat vor/zurück; <b>Aktuelle Woche / Aktueller Monat</b> springt zur Gegenwart.</li>
        </UL>

        <H3>Export (CSV / PDF)</H3>
        <P>Klick auf <b>Export → CSV</b> oder <b>PDF</b> öffnet einen Dialog:</P>
        <UL>
          <li><b>Alle Fahrer</b> oder gezielt einzelne Fahrer (Mehrfachauswahl).</li>
          <li><b>Zeitraum</b>: 1 Woche, 1 Monat, 6 Monate, 1 Jahr oder alles – jeweils <b>ab der aktuell angezeigten Periode rückwärts</b>.</li>
          <li>Ergebnis ist eine <b>Auswertung je Fahrer</b> (Arbeits-, Urlaubs-, Kranktage und Gesamt-Arbeitszeit) im gewählten Format.</li>
        </UL>
        <Note tone="blue">Die Arbeit wird aus manuellen Arbeitszeiten <i>und</i> aus abgeschlossenen Fahrten ermittelt – ohne Doppelzählung.</Note>
      </>
    ),
  },
  {
    id: "urlaub", title: "Urlaub & Krankheit", icon: Stethoscope, roles: STAFF, keywords: "urlaub krank krankheit abwesenheit fehltag eintragen",
    body: (
      <>
        <Lead>Urlaubs- und Krankheitstage werden direkt im Bereich <b>Arbeitszeiterfassung</b> eingetragen und in der Wochenplanung berücksichtigt.</Lead>
        <H3>Urlaub bzw. Krankheitstag erfassen</H3>
        <Steps>
          <li>In der Arbeitszeiterfassung auf <b>Neuer Urlaub</b> bzw. <b>Neuer Krankheitstag</b> klicken.</li>
          <li><b>Mitarbeiter</b> wählen sowie Zeitraum <i>Von – Bis</i> (auch für einzelne Tage gleiches Datum eintragen).</li>
          <li>Optional eine Notiz hinzufügen, dann speichern.</li>
        </Steps>
        <H3>Auswirkungen in der Planung</H3>
        <UL>
          <li>In der Wochenplanung sind betroffene Fahrer im Auswahl-Dropdown farbig markiert (<i>„· Urlaub"</i> / <i>„· Krank"</i>).</li>
          <li>Bestehende Fahrten mit einem abwesenden Fahrer zeigen einen Warnhinweis (<AlertTriangle className="inline h-3 w-3 text-amber-600" />).</li>
          <li>Im Arbeitszeit-Verlauf erscheinen die Tage farbig (<Pill tone="blue">Urlaub</Pill> / <Pill tone="amber">Krank</Pill>) – ohne Stundenanrechnung.</li>
        </UL>
        <H3>Löschen / Korrigieren</H3>
        <P>Über das Mülltonnen-Symbol kann ein Urlaubs- bzw. Krankheitseintrag entfernt werden. Möchtest du den Zeitraum ändern, lösche den Eintrag und lege ihn neu an.</P>
      </>
    ),
  },
  {
    id: "standardzeiten", title: "Arbeitszeitmodell & Zeitkonto-Kategorien", icon: CalendarClock, roles: STAFF, keywords: "arbeitszeitmodell standardzeiten zeitkonto kategorie wochentag regelarbeitszeit vollzeit teilzeit nachtschicht pause sollarbeitszeit vorlage vorbefüllung",
    body: (
      <>
        <Lead>Arbeitszeitmodelle werden pro <b>Zeitkonto-Kategorie</b> gepflegt (z.&nbsp;B. Vollzeit, Teilzeit, Nachtschicht). Ein Fahrer kann optional genau <i>einer</i> Kategorie zugeordnet werden – dadurch sind die Vorgaben gruppiert verwaltbar und liefern die Vorbefüllung bei der Arbeitszeiterfassung.</Lead>

        <H3>Kategorien verwalten</H3>
        <UL>
          <li>Im Bereich <b>Arbeitszeitmodell</b> oben die Kategorie auswählen oder über <b>Neue Kategorie</b> anlegen (z.&nbsp;B. „Teilzeit 30h").</li>
          <li>Über <b>Umbenennen</b> bzw. <b>Löschen</b> lassen sich vorhandene Kategorien anpassen.</li>
          <li>Die Standard-Kategorie ist geschützt und kann nicht gelöscht werden, solange ihr noch Fahrer zugewiesen sind.</li>
          <li>Beim Anlegen eines Fahrers kann die Kategorie optional ausgewählt werden (siehe „Fahrer").</li>
        </UL>

        <H3>Wochentage &amp; Feiertag konfigurieren</H3>
        <Steps>
          <li>Kategorie auswählen – die sieben Wochentage Montag–Sonntag <i>sowie</i> eine eigene Karte <b>Feiertag</b> erscheinen.</li>
          <li>Pro Tag aktivieren (Schalter „Arbeitstag/Frei") und <b>Start-</b> sowie <b>Endzeit</b> setzen.</li>
          <li><b>Mehrere Zeitblöcke je Tag:</b> Über <b>„Weiterer Zeitblock"</b> lassen sich pro Tag mehrere Arbeitsabschnitte mit jeweils eigenen Start-/Endzeiten und Pausen anlegen (z.&nbsp;B. geteilter Dienst). Überzählige Blöcke entfernst du über das Mülltonnen-Symbol.</li>
          <li>Optional je Block eine oder mehrere <b>Standardpausen</b> definieren – diese werden beim manuellen Anlegen einer Arbeitszeit als Vorbefüllung übernommen.</li>
          <li>Pro Tag mit <b>Speichern</b> bestätigen – nur dann werden die Änderungen übernommen.</li>
        </Steps>

        <H3>Verwendung</H3>
        <P>Das Arbeitszeitmodell dient ausschließlich als <b>Soll-Vorgabe/Vorlage</b>. Arbeitszeiten werden <b>nicht</b> automatisch generiert – beim manuellen Anlegen dienen die hinterlegten Zeiten lediglich als Vorbefüllung und lassen sich frei ändern.</P>

        <Note tone="blue">Änderungen wirken nur für künftige Tage. Bereits erfasste Arbeitszeiten bleiben unverändert. Möchtest du nur einzelne Fahrer abweichend planen, lege eine spezielle Kategorie an und weise sie diesen Fahrern zu.</Note>
      </>
    ),
  },

  /* ===== Admin only ===== */
  {
    id: "benutzer", title: "Benutzerverwaltung", icon: UserCog, roles: ADMIN, keywords: "benutzer user rolle passwort admin anlegen verknüpfen sperren",
    body: (
      <>
        <Lead><Pill>Nur Admin</Pill> – hier verwaltest du alle Benutzerkonten und ihre Rollen. Jeder, der sich anmeldet, braucht ein Konto mit genau einer Rolle.</Lead>
        <H3>Benutzer anlegen</H3>
        <Steps>
          <li>Auf <b>Neuer Benutzer</b> klicken.</li>
          <li><b>Benutzername</b>, <b>vollständigen Namen</b> und ein <b>Start-Passwort</b> vergeben.</li>
          <li>Die passende <b>Rolle</b> wählen – Admin, Disponent oder Fahrer (siehe „Rollen &amp; Rechte").</li>
          <li>Speichern – der Benutzer kann sich sofort anmelden.</li>
        </Steps>
        <H3>Fahrer-Konten verknüpfen</H3>
        <P>Damit ein Fahrer nur seine eigenen Fahrten und Arbeitszeiten sieht, verknüpfst du sein Konto mit dem passenden <b>Fahrer-Datensatz</b> (aus dem Bereich „Fahrer"). Ohne diese Verknüpfung sieht ein Fahrer-Konto keine Fahrten.</P>
        <H3>Passwörter & Konto-Status</H3>
        <UL>
          <li><b>Passwort zurücksetzen:</b> Wenn ein Benutzer sein Passwort vergessen hat, kannst du ein neues vergeben.</li>
          <li><b>Aktiv/Inaktiv:</b> Deaktivierte Konten können sich nicht mehr anmelden, bleiben aber für die Nachvollziehbarkeit erhalten – besser als Löschen.</li>
          <li>Benutzernamen sind eindeutig und werden klein geschrieben gespeichert.</li>
        </UL>
        <Note tone="amber">Lege mindestens ein zweites Admin-Konto an, damit du dich nie selbst aussperrst. Vergib sichere Passwörter und ändere Standard-Zugänge nach dem ersten Login.</Note>
      </>
    ),
  },
  {
    id: "audit", title: "Audit-Log", icon: FileText, roles: ADMIN, keywords: "audit log protokoll änderungen nachvollziehen historie dsgvo wer wann diff felder",
    body: (
      <>
        <Lead><Pill>Nur Admin</Pill> – das Audit-Log ist ein fortlaufendes, fälschungssicheres Protokoll aller wichtigen Aktionen im System. So bleibt jederzeit nachvollziehbar, wer was geändert hat.</Lead>

        <H3>Was wird protokolliert?</H3>
        <UL>
          <li><b>Anlegen</b> (create) – z.&nbsp;B. neue Fahrt, neuer Fahrer, neuer Benutzer.</li>
          <li><b>Ändern</b> (update) – inkl. Liste der betroffenen Felder (z.&nbsp;B. „driver_id, start_time").</li>
          <li><b>Löschen</b> (delete) – entfernte Datensätze.</li>
          <li><b>Kopieren</b> (duplicate) – z.&nbsp;B. eine Fahrt, die auf andere Tage kopiert wurde, inkl. Zieltage.</li>
          <li><b>Anmelde-Ereignisse</b> (login/logout) sowie fehlgeschlagene Anmeldeversuche.</li>
        </UL>

        <H3>Spalten im Protokoll</H3>
        <UL>
          <li><b>Zeitpunkt</b> – wann die Aktion ausgeführt wurde (UTC + Browser-Zeitzone).</li>
          <li><b>Benutzer</b> – wer die Aktion ausgelöst hat.</li>
          <li><b>Aktion &amp; Objekt</b> – Art der Änderung und betroffener Datensatz (mit Verweis-ID).</li>
          <li><b>Details</b> – bei Änderungen werden die geänderten Felder aufgelistet; bei Kopier-Aktionen die Zieltage.</li>
        </UL>

        <Note tone="blue">Das Audit-Log unterstützt die DSGVO-Konformität und ist bei Rückfragen die zuverlässige Quelle, um Änderungen lückenlos zu belegen. Einträge können <i>nicht</i> nachträglich verändert werden – auch nicht durch den Administrator.</Note>
      </>
    ),
  },

  /* ===== Tipps - all ===== */
  {
    id: "tipps", title: "Tipps & FAQ", icon: Lightbulb, roles: ALL, keywords: "tipps faq hilfe fragen probleme tastatur shortcut suche",
    body: (
      <>
        <Lead>Antworten auf häufige Fragen und praktische Hinweise für die tägliche Arbeit.</Lead>

        <H3>Häufige Fragen</H3>
        <UL>
          <li><b>Warum sehe ich manche Menüpunkte nicht?</b> Die Sichtbarkeit hängt von deiner Rolle ab (siehe „Rollen &amp; Rechte"). Fahrer sehen z.&nbsp;B. keine Benutzerverwaltung oder Arbeitszeitmodell.</li>
          <li><b>Wie wechsle ich zwischen hellem und dunklem Design?</b> Über das Sonnen-/Mond-Symbol oben rechts (siehe „Topbar &amp; Navigation").</li>
          <li><b>Wie funktioniert die globale Suche?</b> Als Admin/Disponent tippst du einen Namen, eine Personalnummer, Telefonnummer oder ein Kennzeichen ein – ein Live-Dropdown zeigt passende Fahrer und Fahrzeuge, ein Klick öffnet direkt die Detailkarte. Mit „Fahrten durchsuchen nach …" (oder Enter) gelangst du in die gefilterte Fahrtenliste. Fahrer nutzen die einfache Fahrten-Suche.</li>
          <li><b>Ich habe mein Passwort vergessen.</b> Bitte deinen Administrator, dir ein neues Passwort zu vergeben.</li>
          <li><b>Was bedeuten die Farben in der Timeline?</b> Jede Farbe steht für einen Fahrer (Legende unten). Ein <span className="text-red-600 font-semibold">roter Rahmen</span> = Fahrer-Konflikt, ein <span className="text-amber-600 font-semibold">gelber Rahmen</span> = Fahrzeug-Konflikt zur selben Zeit.</li>
          <li><b>Wie ziehe ich eine Fahrt auf einen anderen Tag?</b> In der Timeline einfach den Balken in eine andere Tageszeile ziehen – das Datum wird automatisch übernommen. In der Listenansicht funktioniert dies per Drag &amp; Drop der Fahrtenkarte zwischen den Spalten.</li>
          <li><b>Wie weise ich eine Fahrt einem anderen Fahrer zu?</b> Den Timeline-Balken in die Fahrer-Spur der Zielperson ziehen (auch tagesübergreifend). Auf die „ohne Fahrer"-Spur ziehen entfernt die Zuweisung.</li>
          <li><b>Eine Fahrt hat keinen Fahrer – woran erkenne ich das?</b> Solche Fahrten sind rot markiert und werden oben in der Wochenplanung als „ohne Fahrer" gezählt; in der Timeline erscheinen sie in der untersten Spur des Tages.</li>
          <li><b>Wie sehe ich die Wochenstunden je Fahrer?</b> Die Karte <b>Fahrer-Übersicht der Woche</b> oberhalb von Liste/Timeline listet alle Fahrer mit Touren-Anzahl und geplanten Stunden.</li>
          <li><b>Was sind Zeitkonto-Kategorien?</b> Gruppen wie Vollzeit/Teilzeit/Nachtschicht, denen Arbeitszeitmodell zugeordnet sind. Jeder Fahrer gehört zu genau einer Kategorie (siehe „Arbeitszeitmodell &amp; Zeitkonto-Kategorien").</li>
        </UL>

        <H3>Praktische Tipps</H3>
        <UL>
          <li>In der Timeline lassen sich Balken ziehen (verschieben), in der Länge anpassen, auf andere Tage <i>und</i> auf andere Fahrer-Spuren verschieben – den <b>Zoom</b> (30&nbsp;Min bis 24&nbsp;Std) wählst du oben rechts; das Andock-Raster liegt fest bei 15&nbsp;Min.</li>
          <li><b>Klickbare Namen &amp; Kennzeichen:</b> Fahrernamen öffnen überall (Dashboard, Fahrten, Arbeitszeit, Wochenplanung) die Fahrer-Detailkarte, Kennzeichen/Fahrzeuge die Fahrzeug-Detailkarte.</li>
          <li>Inaktive Fahrer/Fahrzeuge verschwinden automatisch aus den Auswahllisten – so bleibt die Planung übersichtlich.</li>
          <li>Nutze die <b>Export-Funktion</b> im Fahrten-Tab (CSV/PDF), um Auswertungen weiterzugeben oder zu archivieren. Filter werden dabei berücksichtigt.</li>
          <li>Bei Zeitkonflikten beim Verschieben erscheint vorab eine Sicherheitsabfrage – so vermeidest du Doppelbelegungen.</li>
          <li>Beim <b>Kopieren</b> einer Fahrt auf mehrere Tage werden Adressen, Zeiten, Fahrer und Fahrzeug mitkopiert. Der Schalter „Status auf Geplant zurücksetzen" kann dabei den Status der Kopien zurücksetzen.</li>
          <li>Im Fahrer-Detail (Klick auf Namen) findest du eine Tages-Übersicht mit Schichtberechnung, Urlauben und allen Fahrten dieses Tages.</li>
        </UL>

        <Note tone="blue">Noch Fragen? Wende dich an deinen Administrator – er kann Benutzer, Rollen und Stammdaten anpassen.</Note>
      </>
    ),
  },
];

export default function Docs() {
  const { user } = useAuth();
  const [query, setQuery] = useState("");

  // Filter sections by role first (developer sees everything)
  const visibleSections = useMemo(() => {
    if (user.role === "developer") return SECTIONS;
    return SECTIONS.filter((s) => !s.roles || s.roles.includes(user.role));
  }, [user.role]);

  const [active, setActive] = useState(visibleSections[0]?.id);
  const contentRef = useRef(null);
  const sectionRefs = useRef({});

  // Keep active section in sync when role-filtered list changes
  useEffect(() => {
    if (!visibleSections.find((s) => s.id === active)) {
      setActive(visibleSections[0]?.id);
    }
  }, [visibleSections, active]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleSections;
    return visibleSections.filter(
      (s) => s.title.toLowerCase().includes(q) || (s.keywords || "").includes(q)
    );
  }, [query, visibleSections]);

  // Scroll-spy: highlight the section currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [filtered]);

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActive(id);
  };

  return (
    <div className="space-y-4" data-testid="docs-page">
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-blue-600 text-white p-2"><BookOpen className="h-6 w-6" /></div>
        <div>
          <h1 className="text-2xl heading font-semibold">Docs</h1>
          <p className="text-sm text-muted-foreground">
            {user.role === "fahrer"
              ? "Dein Handbuch – die für dich relevanten Bereiche auf einen Blick."
              : "Handbuch & Anleitung – alles zum System auf einen Blick."}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* Sidebar / Table of contents */}
        <aside className="lg:sticky lg:top-[5rem] self-start space-y-3 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Dokumentation durchsuchen…"
              className="pl-8"
              data-testid="docs-search"
            />
          </div>
          <nav className="rounded-lg border border-border bg-card p-2" data-testid="docs-toc">
            {filtered.length === 0 && <p className="px-2 py-3 text-xs text-muted-foreground italic">Keine Treffer.</p>}
            {filtered.map((s) => {
              const Icon = s.icon;
              const isActive = active === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  data-testid={`docs-nav-${s.id}`}
                  className={`w-full text-left flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors ${
                    isActive ? "bg-blue-600 text-white font-medium" : "hover:bg-muted text-foreground/80"
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{s.title}</span>
                </button>
              );
            })}
          </nav>
          <p className="px-1 text-[11px] text-muted-foreground">Angemeldet als <span className="font-medium text-foreground">{user.full_name || user.username}</span> · Rolle {user.role}.</p>
        </aside>

        {/* Content */}
        <article ref={contentRef} className="space-y-10 max-w-3xl">
          {filtered.map((s) => {
            const Icon = s.icon;
            return (
              <section
                key={s.id}
                id={s.id}
                ref={(el) => (sectionRefs.current[s.id] = el)}
                className="scroll-mt-20"
                data-testid={`docs-section-${s.id}`}
              >
                <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-border">
                  <Icon className="h-5 w-5 text-blue-600" />
                  <h2 className="text-xl heading font-semibold">{s.title}</h2>
                </div>
                <div className="space-y-3">{s.body}</div>
              </section>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-muted-foreground italic">Keine Abschnitte gefunden. Suchbegriff anpassen.</p>
          )}
        </article>
      </div>
    </div>
  );
}
